import React, { useState } from 'react';
import { Button, Spin } from 'antd';
import dayjs from 'dayjs';
import { BudgetStatus } from 'types/marketing';
import { centsToYuan } from 'utils/marketing';
import usePracticePoolBudget from 'hooks/marketing/usePracticePoolBudget';
import usePracticeOverview from 'hooks/marketing/usePracticeOverview';
import EmptyIcon from 'assets/common/empty.svg';
import GroupProlistingModal from './GroupProlistingModal';
import s from './s.module.less';

interface IProps {
    organizationId: number;
}

const PERIOD_LABEL: Record<string, string> = {
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
};

// Ops view of a group practice's pool Pro-Listings campaign.
// Mirrors the per-provider ProListings card; data comes from the OPS org-scoped endpoints.
// With no campaign yet the same modal doubles as the first-time setup (backend is saveOrUpdate).
const GroupProlistings: React.FC<IProps> = ({ organizationId }) => {
    const [poolBudget, poolLoading, refetchPool] = usePracticePoolBudget(true, organizationId);
    const [overview, overviewLoading, refetchOverview] = usePracticeOverview(true, organizationId);
    const [modalVisible, setModalVisible] = useState(false);

    const pool = poolBudget?.pool;
    const hasCampaign = !!pool;

    const isActive = pool?.status === BudgetStatus.ACTIVE;

    const renderEmptyState = () => (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-medium">Campaign</h2>
                <Button
                    type="primary"
                    className={s.editButton}
                    onClick={() => {
                        setModalVisible(true);
                    }}
                >
                    Set up campaign
                </Button>
            </div>
            <div className="flex items-center flex-col">
                <div className="text-xl font-medium">No data</div>
                <img src={EmptyIcon} alt="empty" />
            </div>
        </div>
    );

    const renderCampaignCard = () => (
        <div className={s.campaignCard}>
            <div className={s.cardHeader}>
                <h3 className={s.cardTitle}>Campaign</h3>
                <Button
                    className={s.editButton}
                    onClick={() => {
                        setModalVisible(true);
                    }}
                >
                    Edit
                </Button>
            </div>

            <div className={s.cardContent}>
                <div className={s.fieldGroup}>
                    <div className={s.fieldLabel}>Status</div>
                    <div className={`${s.fieldValue} ${isActive ? s.statusActive : s.statusInactive}`}>
                        {pool?.status}
                    </div>
                </div>

                <div className={s.fieldGroup}>
                    <div className={s.fieldLabel}>Bid per booking</div>
                    <div className={s.fieldValue}>${centsToYuan(poolBudget?.defaultBidAmountCents)}</div>
                </div>

                <div className={s.fieldGroup}>
                    <div className={s.fieldLabel}>Budget type</div>
                    <div className={s.fieldValue}>
                        {PERIOD_LABEL[pool?.budgetPeriod || ''] || pool?.budgetPeriod || '-'}
                    </div>
                </div>

                <div className={s.fieldGroup}>
                    <div className={s.fieldLabel}>Budget amount</div>
                    <div className={s.fieldValue}>${centsToYuan(pool?.budgetCapCents)}</div>
                </div>

                <div className={s.fieldGroup}>
                    <div className={s.fieldLabel}>Last modified</div>
                    <div className={s.fieldValue}>
                        {overview?.lastModifiedAt ? dayjs(overview.lastModifiedAt).format('MM/DD/YYYY') : '-'}
                    </div>
                </div>

                <div className={s.fieldGroup}>
                    <div className={s.fieldLabel}>Last modified by</div>
                    <div className={s.fieldValue}>{overview?.lastModifiedBy || '-'}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={s.container}>
            <Spin spinning={poolLoading || overviewLoading}>
                {hasCampaign ? renderCampaignCard() : renderEmptyState()}
            </Spin>

            {modalVisible && (
                <GroupProlistingModal
                    visible={modalVisible}
                    organizationId={organizationId}
                    poolBudget={poolBudget}
                    stdListingCount={overview?.totalProviders ?? 0}
                    poolExhausted={overview?.poolStatus === BudgetStatus.BUDGET_EXHAUSTED}
                    hasJoinedProListing={!!overview?.createdAt}
                    remainingBudget={
                        overview?.poolCapCents != null && overview?.poolSpentCents != null
                            ? centsToYuan(Math.max(0, overview.poolCapCents - overview.poolSpentCents))
                            : undefined
                    }
                    lastModifiedAt={overview?.lastModifiedAt}
                    lastModifiedBy={overview?.lastModifiedBy}
                    onCancel={() => setModalVisible(false)}
                    onSaved={() => {
                        refetchPool();
                        refetchOverview();
                    }}
                />
            )}
        </div>
    );
};

export default GroupProlistings;
